import React, { useState, useRef, useEffect } from 'react';

// Define types for our component
interface SqlToErDiagramProps {}

// Define the structure for tables and relationships
interface TableColumn {
  name: string;
  type: string;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
  references?: {
    table: string;
    column: string;
  };
  isNullable: boolean;
}

interface Table {
  name: string;
  columns: TableColumn[];
  position: {
    x: number;
    y: number;
  };
}

interface Relationship {
  id: string;
  sourceTable: string;
  sourceColumn: string;
  targetTable: string;
  targetColumn: string;
  type: 'one-to-one' | 'one-to-many' | 'many-to-many';
}

// Parser function for SQL CREATE TABLE statements
const parseCreateTableStatements = (sql: string): Table[] => {
  // Remove SQL comments
  let cleanSql = sql.replace(/--.*$/gm, '');
  cleanSql = cleanSql.replace(/\/\*[\s\S]*?\*\//gm, '');
  
  // Regular expression to match CREATE TABLE statements
  const createTableRegex = /CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?[`"']?([^`"'\s]+)[`"']?\s*\(([\s\S]*?)(?:\)\s*(?:ENGINE|DEFAULT|CHARACTER|COLLATE|AUTO_INCREMENT).*?|;|\)$)/gim;
  
  // Regular expression to match column definitions
  const columnRegex = /\s*[`"']?([^`"'\s,]+)[`"']?\s+([^\s,(]+(?:\([^)]*\))?)[^,]*(?:,|$)/gim;
  
  // Regular expression to match primary key constraints
  const primaryKeyRegex = /PRIMARY\s+KEY\s*\(\s*[`"']?([^`"'\s,)]+)[`"']?(?:\s*,\s*[`"']?([^`"'\s,)]+)[`"']?)*\s*\)/gim;
  
  // Regular expression to match foreign key constraints
  const foreignKeyRegex = /FOREIGN\s+KEY\s*\(\s*[`"']?([^`"'\s,)]+)[`"']?\s*\)\s*REFERENCES\s+[`"']?([^`"'\s,)]+)[`"']?\s*\(\s*[`"']?([^`"'\s,)]+)[`"']?\s*\)/gim;
  
  const tables: Table[] = [];
  let match;
  let yPosition = 50;
  
  // Find all CREATE TABLE statements
  while ((match = createTableRegex.exec(cleanSql)) !== null) {
    const tableName = match[1];
    const tableBody = match[2];
    
    // Create a new table object
    const table: Table = {
      name: tableName,
      columns: [],
      position: {
        x: 50 + (tables.length % 3) * 350,
        y: yPosition + Math.floor(tables.length / 3) * 350
      }
    };
    
    // Extract primary keys
    const primaryKeys: string[] = [];
    let pkMatch;
    while ((pkMatch = primaryKeyRegex.exec(tableBody)) !== null) {
      for (let i = 1; i < pkMatch.length; i++) {
        if (pkMatch[i]) primaryKeys.push(pkMatch[i]);
      }
    }
    
    // Extract foreign keys
    const foreignKeys: Map<string, { table: string; column: string }> = new Map();
    let fkMatch;
    while ((fkMatch = foreignKeyRegex.exec(tableBody)) !== null) {
      foreignKeys.set(fkMatch[1], {
        table: fkMatch[2],
        column: fkMatch[3]
      });
    }
    
    // Extract columns
    let colMatch;
    columnRegex.lastIndex = 0; // Reset regex index
    while ((colMatch = columnRegex.exec(tableBody)) !== null) {
      const columnName = colMatch[1];
      const columnType = colMatch[2];
      
      // Skip constraint definitions that might be matched as columns
      if (["CONSTRAINT", "PRIMARY", "FOREIGN", "UNIQUE", "CHECK", "INDEX"].includes(columnName.toUpperCase())) {
        continue;
      }
      
      table.columns.push({
        name: columnName,
        type: columnType,
        isPrimaryKey: primaryKeys.includes(columnName),
        isForeignKey: foreignKeys.has(columnName),
        references: foreignKeys.get(columnName),
        isNullable: !colMatch[0].toUpperCase().includes("NOT NULL")
      });
    }
    
    tables.push(table);
  }
  
  return tables;
};

// Function to extract relationships from tables
const extractRelationships = (tables: Table[]): Relationship[] => {
  const relationships: Relationship[] = [];
  
  tables.forEach(sourceTable => {
    sourceTable.columns.forEach(column => {
      if (column.isForeignKey && column.references) {
        relationships.push({
          id: `${sourceTable.name}_${column.name}_${column.references.table}_${column.references.column}`,
          sourceTable: sourceTable.name,
          sourceColumn: column.name,
          targetTable: column.references.table,
          targetColumn: column.references.column,
          // By default assume one-to-many relationship, could be refined based on constraints
          type: 'one-to-many'
        });
      }
    });
  });
  
  return relationships;
};

// Draw a line between two elements on the canvas
const drawRelationship = (
  ctx: CanvasRenderingContext2D, 
  sourceX: number, 
  sourceY: number, 
  targetX: number, 
  targetY: number, 
  type: 'one-to-one' | 'one-to-many' | 'many-to-many'
) => {
  ctx.beginPath();
  ctx.moveTo(sourceX, sourceY);
  
  // Create a bezier curve for the line
  const controlPointX1 = sourceX + (targetX - sourceX) / 3;
  const controlPointY1 = sourceY;
  const controlPointX2 = sourceX + 2 * (targetX - sourceX) / 3;
  const controlPointY2 = targetY;
  
  ctx.bezierCurveTo(controlPointX1, controlPointY1, controlPointX2, controlPointY2, targetX, targetY);
  ctx.stroke();
  
  // Draw arrow at the target end
  const angle = Math.atan2(targetY - controlPointY2, targetX - controlPointX2);
  const arrowSize = 10;
  
  ctx.beginPath();
  ctx.moveTo(targetX, targetY);
  
  // Draw relationship symbols based on type
  if (type === 'one-to-many') {
    // Draw "one" symbol at the source (a vertical line)
    const oneSymbolX = sourceX + 15;
    ctx.moveTo(oneSymbolX, sourceY - 8);
    ctx.lineTo(oneSymbolX, sourceY + 8);
    ctx.stroke();
    
    // Draw "many" symbol at the target (crow's foot)
    ctx.beginPath();
    ctx.moveTo(targetX, targetY);
    ctx.lineTo(targetX - arrowSize * Math.cos(angle - Math.PI/6), targetY - arrowSize * Math.sin(angle - Math.PI/6));
    ctx.moveTo(targetX, targetY);
    ctx.lineTo(targetX - arrowSize * Math.cos(angle + Math.PI/6), targetY - arrowSize * Math.sin(angle + Math.PI/6));
    ctx.stroke();
  } else if (type === 'one-to-one') {
    // Draw "one" symbols at both ends
    const sourceOneX = sourceX + 15;
    ctx.moveTo(sourceOneX, sourceY - 8);
    ctx.lineTo(sourceOneX, sourceY + 8);
    
    const targetOneX = targetX - 15;
    ctx.moveTo(targetOneX, targetY - 8);
    ctx.lineTo(targetOneX, targetY + 8);
    ctx.stroke();
  } else if (type === 'many-to-many') {
    // Draw "many" symbols at both ends
    // Source end
    ctx.beginPath();
    const reverseAngle = Math.atan2(sourceY - controlPointY1, sourceX - controlPointX1);
    ctx.moveTo(sourceX, sourceY);
    ctx.lineTo(sourceX - arrowSize * Math.cos(reverseAngle - Math.PI/6), sourceY - arrowSize * Math.sin(reverseAngle - Math.PI/6));
    ctx.moveTo(sourceX, sourceY);
    ctx.lineTo(sourceX - arrowSize * Math.cos(reverseAngle + Math.PI/6), sourceY - arrowSize * Math.sin(reverseAngle + Math.PI/6));
    
    // Target end
    ctx.moveTo(targetX, targetY);
    ctx.lineTo(targetX - arrowSize * Math.cos(angle - Math.PI/6), targetY - arrowSize * Math.sin(angle - Math.PI/6));
    ctx.moveTo(targetX, targetY);
    ctx.lineTo(targetX - arrowSize * Math.cos(angle + Math.PI/6), targetY - arrowSize * Math.sin(angle + Math.PI/6));
    ctx.stroke();
  }
};

// Draw the ER diagram on a canvas
const drawERDiagram = (
  ctx: CanvasRenderingContext2D,
  tables: Table[],
  relationships: Relationship[],
  tableElements: Map<string, DOMRect>
) => {
  // Clear the canvas
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  
  // Draw relationships
  ctx.strokeStyle = '#007BFF';
  ctx.lineWidth = 2;
  
  relationships.forEach(rel => {
    const sourceElement = tableElements.get(rel.sourceTable);
    const targetElement = tableElements.get(rel.targetTable);
    
    if (sourceElement && targetElement) {
      // Find the correct column positions inside each table
      const sourceColumns = document.querySelectorAll(`[data-table="${rel.sourceTable}"] [data-column]`);
      const targetColumns = document.querySelectorAll(`[data-table="${rel.targetTable}"] [data-column]`);
      
      let sourceColumnElement: Element | null = null;
      let targetColumnElement: Element | null = null;
      
      sourceColumns.forEach(col => {
        if (col.getAttribute('data-column') === rel.sourceColumn) {
          sourceColumnElement = col;
        }
      });
      
      targetColumns.forEach(col => {
        if (col.getAttribute('data-column') === rel.targetColumn) {
          targetColumnElement = col;
        }
      });
      
      let sourceX, sourceY, targetX, targetY;
      
      if (sourceColumnElement && targetColumnElement) {
        const sourceColRect = (sourceColumnElement as HTMLElement).getBoundingClientRect();
        const targetColRect = (targetColumnElement as HTMLElement).getBoundingClientRect();
        
        // Adjust for canvas position
        sourceX = sourceColRect.right - ctx.canvas.getBoundingClientRect().left;
        sourceY = sourceColRect.top + sourceColRect.height/2 - ctx.canvas.getBoundingClientRect().top;
        targetX = targetColRect.left - ctx.canvas.getBoundingClientRect().left;
        targetY = targetColRect.top + targetColRect.height/2 - ctx.canvas.getBoundingClientRect().top;
      } else {
        // Fallback to table centers if column elements can't be found
        sourceX = sourceElement.left + sourceElement.width - ctx.canvas.getBoundingClientRect().left;
        sourceY = sourceElement.top + sourceElement.height/2 - ctx.canvas.getBoundingClientRect().top;
        targetX = targetElement.left - ctx.canvas.getBoundingClientRect().left;
        targetY = targetElement.top + targetElement.height/2 - ctx.canvas.getBoundingClientRect().top;
      }
      
      drawRelationship(ctx, sourceX, sourceY, targetX, targetY, rel.type);
    }
  });
};

// Main component
const SqlToErDiagram: React.FC<SqlToErDiagramProps> = () => {
  // State for SQL input
  const [sqlInput, setSqlInput] = useState<string>('');
  
  // State for parsed tables and relationships
  const [tables, setTables] = useState<Table[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  
  // State for error messages
  const [error, setError] = useState<string>('');
  
  // State for diagram view options
  const [showTypes, setShowTypes] = useState<boolean>(true);
  const [showNullable, setShowNullable] = useState<boolean>(true);
  
  // State for dragging tables
  const [draggedTable, setDraggedTable] = useState<string | null>(null);
  const [dragStartPosition, setDragStartPosition] = useState<{x: number, y: number} | null>(null);
  
  // Refs for the canvas and container
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Map to store table element positions
  const [tableElements, setTableElements] = useState<Map<string, DOMRect>>(new Map());

  // Function to parse SQL and generate diagram
  const generateErDiagram = () => {
    try {
      if (!sqlInput.trim()) {
        setError('Please enter SQL CREATE TABLE statements');
        return;
      }
      
      setError('');
      const parsedTables = parseCreateTableStatements(sqlInput);
      
      if (parsedTables.length === 0) {
        setError('No valid CREATE TABLE statements found');
        return;
      }
      
      setTables(parsedTables);
      setRelationships(extractRelationships(parsedTables));
    } catch (e) {
      setError(`Error parsing SQL: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Use example CREATE TABLE statements
  const useExampleSql = () => {
    const exampleSql = `
CREATE TABLE users (
  user_id INT PRIMARY KEY,
  username VARCHAR(50) NOT NULL,
  email VARCHAR(100) NOT NULL,
  password_hash VARCHAR(255) NOT NULL,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  last_login TIMESTAMP
);

CREATE TABLE categories (
  category_id INT PRIMARY KEY,
  name VARCHAR(50) NOT NULL,
  description TEXT
);

CREATE TABLE products (
  product_id INT PRIMARY KEY,
  category_id INT NOT NULL,
  name VARCHAR(100) NOT NULL,
  description TEXT,
  price DECIMAL(10, 2) NOT NULL,
  stock_quantity INT NOT NULL DEFAULT 0,
  FOREIGN KEY (category_id) REFERENCES categories(category_id)
);

CREATE TABLE orders (
  order_id INT PRIMARY KEY,
  user_id INT NOT NULL,
  order_date TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  status VARCHAR(20) NOT NULL,
  total_amount DECIMAL(10, 2) NOT NULL,
  shipping_address TEXT NOT NULL,
  FOREIGN KEY (user_id) REFERENCES users(user_id)
);

CREATE TABLE order_items (
  order_item_id INT PRIMARY KEY,
  order_id INT NOT NULL,
  product_id INT NOT NULL,
  quantity INT NOT NULL,
  price_per_unit DECIMAL(10, 2) NOT NULL,
  FOREIGN KEY (order_id) REFERENCES orders(order_id),
  FOREIGN KEY (product_id) REFERENCES products(product_id)
);
`;
    setSqlInput(exampleSql);
    setError('');
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setSqlInput('');
    setTables([]);
    setRelationships([]);
    setError('');
  };

  // Handle mouse down for dragging
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>, tableName: string) => {
    setDraggedTable(tableName);
    setDragStartPosition({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (draggedTable && dragStartPosition) {
      const dx = e.clientX - dragStartPosition.x;
      const dy = e.clientY - dragStartPosition.y;
      
      setTables(prev => prev.map(table => {
        if (table.name === draggedTable) {
          return {
            ...table,
            position: {
              x: table.position.x + dx,
              y: table.position.y + dy
            }
          };
        }
        return table;
      }));
      
      setDragStartPosition({ x: e.clientX, y: e.clientY });
    }
  };

  // Handle mouse up to end dragging
  const handleMouseUp = () => {
    setDraggedTable(null);
    setDragStartPosition(null);
  };

  // Update table element positions and redraw relationships
  useEffect(() => {
    if (tables.length > 0) {
      const newTableElements = new Map<string, DOMRect>();
      
      // Get all table element positions
      tables.forEach(table => {
        const tableElement = document.querySelector(`[data-table="${table.name}"]`) as HTMLElement;
        if (tableElement) {
          newTableElements.set(table.name, tableElement.getBoundingClientRect());
        }
      });
      
      setTableElements(newTableElements);
      
      // Redraw relationships on the canvas
      const canvas = canvasRef.current;
      const container = containerRef.current;
      
      if (canvas && container && newTableElements.size > 0) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Set canvas size to match container
          canvas.width = container.scrollWidth;
          canvas.height = container.scrollHeight;
          
          // Draw relationships
          drawERDiagram(ctx, tables, relationships, newTableElements);
        }
      }
    }
  }, [tables, relationships]);

  // Redraw relationships when window is resized
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      
      if (canvas && container && tableElements.size > 0) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Update canvas size
          canvas.width = container.scrollWidth;
          canvas.height = container.scrollHeight;
          
          // Redraw
          drawERDiagram(ctx, tables, relationships, tableElements);
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tables, relationships, tableElements]);

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">SQL to ER Diagram Converter</h1>

      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Generate an Entity-Relationship diagram from SQL CREATE TABLE statements. 
          Simply paste your SQL schema and visualize the tables and their relationships.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Input Section */}
        <div className="bg-white p-4 rounded-md shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <label htmlFor="sqlInput" className="font-medium text-gray-700">
              SQL CREATE TABLE Statements
            </label>
            <div className="flex space-x-2">
              <button
                className="text-sm text-blue-600 hover:text-blue-800"
                onClick={useExampleSql}
              >
                Use Example
              </button>
              <button
                className="text-sm text-gray-600 hover:text-gray-800"
                onClick={() => setSqlInput('')}
              >
                Clear
              </button>
            </div>
          </div>

          <textarea
            id="sqlInput"
            className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
            value={sqlInput}
            onChange={(e) => setSqlInput(e.target.value)}
            placeholder="Enter your CREATE TABLE statements here..."
          />

          <div className="mt-4 flex justify-between">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={generateErDiagram}
            >
              Generate ER Diagram
            </button>

            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              onClick={clearAll}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Display Options */}
        {tables.length > 0 && (
          <div className="bg-white p-4 rounded-md shadow-sm">
            <h3 className="font-medium text-gray-700 mb-3">Display Options</h3>
            <div className="flex space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showTypes"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={showTypes}
                  onChange={(e) => setShowTypes(e.target.checked)}
                />
                <label htmlFor="showTypes" className="ml-2 block text-sm text-gray-700">
                  Show Column Types
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showNullable"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={showNullable}
                  onChange={(e) => setShowNullable(e.target.checked)}
                />
                <label htmlFor="showNullable" className="ml-2 block text-sm text-gray-700">
                  Show Nullable Indicators
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
        )}

        {/* ER Diagram Display */}
        {tables.length > 0 && (
          <div 
            className="bg-white p-4 rounded-md shadow-sm overflow-auto" 
            style={{ minHeight: '700px' }}
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <h3 className="font-medium text-gray-700 mb-3">ER Diagram</h3>
            <p className="text-sm text-gray-600 mb-4">
              <span className="italic">Tip:</span> You can drag tables to rearrange them.
            </p>
            
            <div className="relative">
              {/* Canvas for drawing relationships */}
              <canvas 
                ref={canvasRef} 
                className="absolute top-0 left-0 w-full h-full pointer-events-none"
              />
              
              {/* Tables */}
              <div className="relative">
                {tables.map((table) => (
                  <div
                    key={table.name}
                    className="absolute bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden"
                    style={{
                      left: `${table.position.x}px`,
                      top: `${table.position.y}px`,
                      minWidth: '250px',
                    }}
                    data-table={table.name}
                    onMouseDown={(e) => handleMouseDown(e, table.name)}
                    title="Drag to move table"
                  >
                    {/* Table Header */}
                    <div className="bg-blue-100 p-2 cursor-move">
                      <h4 className="font-bold text-blue-800">{table.name}</h4>
                    </div>
                    
                    {/* Table Columns */}
                    <div className="p-2">
                      {table.columns.map((column, index) => (
                        <div 
                          key={`${table.name}-${column.name}`} 
                          className="py-1 border-b border-gray-100 flex items-center"
                          data-column={column.name}
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              {column.isPrimaryKey && (
                                <span className="mr-1 text-yellow-600" title="Primary Key">🔑</span>
                              )}
                              {column.isForeignKey && (
                                <span className="mr-1 text-blue-600" title="Foreign Key">🔗</span>
                              )}
                              <span className="font-medium">{column.name}</span>
                            </div>
                          </div>
                          <div className="text-sm text-gray-600">
                            {showTypes && column.type} 
                            {showNullable && (
                              <span className={column.isNullable ? "text-gray-400" : "text-red-500"}>
                                {column.isNullable ? " NULL" : " NOT NULL"}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tables.length === 0 && !error && (
          <div className="bg-gray-50 border border-gray-200 text-gray-500 p-8 rounded-md flex items-center justify-center">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-lg font-medium mb-2">No ER Diagram Generated Yet</p>
              <p className="max-w-md mx-auto">
                Enter your SQL CREATE TABLE statements in the text area above and click "Generate ER Diagram" to visualize your database schema.
              </p>
            </div>
          </div>
        )}

        {/* Information Section */}
        <div className="bg-white p-6 rounded-md shadow-sm">
          <h2 className="text-lg font-semibold mb-4">About SQL to ER Diagram Converter</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-800 mb-2">What This Tool Does</h3>
              <p className="text-gray-600">
                This tool converts SQL CREATE TABLE statements into an interactive Entity-Relationship diagram. 
                It parses the SQL statements, extracts table structures, and visualizes the relationships between tables.
              </p>
            </div>

            <div>
              <h3 className="font-medium text-gray-800 mb-2">Features</h3>
              <ul className="list-disc pl-5 text-gray-600">
                <li><strong>Visual Schema Representation</strong> - See your database tables and relationships in a graphical format</li>
                <li><strong>Interactive Diagram</strong> - Drag tables to arrange them as needed</li>
                <li><strong>Relationship Visualization</strong> - Show connections between tables based on foreign keys</li>
                <li><strong>Primary and Foreign Key Highlighting</strong> - Easily identify key columns</li>
                <li><strong>Display Options</strong> - Toggle column types and nullable indicators</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-gray-800 mb-2">Supported SQL Syntax</h3>
              <ul className="list-disc pl-5 text-gray-600">
                <li>Standard CREATE TABLE statements</li>
                <li>Column definitions with data types</li>
                <li>PRIMARY KEY constraints</li>
                <li>FOREIGN KEY constraints with REFERENCES</li>
                <li>NOT NULL constraints</li>
                <li>Default values</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-gray-800 mb-2">Limitations</h3>
              <ul className="list-disc pl-5 text-gray-600">
                <li>Limited support for complex SQL dialects (focuses on standard SQL syntax)</li>
                <li>May not detect all relationship types or complex constraints</li>
                <li>Does not support composite keys or complex relationship rules</li>
                <li>Handles only CREATE TABLE statements (not ALTER TABLE, etc.)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-gray-800 mb-2">How to Use</h3>
              <ol className="list-decimal pl-5 text-gray-600">
                <li>Paste your SQL CREATE TABLE statements in the input area</li>
                <li>Click "Generate ER Diagram" to visualize your schema</li>
                <li>Drag tables to rearrange them for better visualization</li>
                <li>Use the display options to customize the diagram</li>
                <li>Analyze relationships between your tables</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqlToErDiagram;